import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  inject,
} from '@angular/core';
import { Negotiation } from '../../core/generated/model/negotiation';
import {
  NegotiationControllerService, ChatNegotiationControllerService, ProfileProviderPJ, ProfileProviderPF
} from '../../core/generated';
import { MaterialModule } from '../material/material.module';
import { MatDialog } from '@angular/material/dialog';
import { DeviceDetectorService } from 'ngx-device-detector';
import { register, SwiperContainer } from 'swiper/element';
import { FormControl, FormsModule } from '@angular/forms';
import { UserPreferencesCacheService } from '../services/user-preferences-cache.service';
import { AuthCustomService } from '../../security/auth-custom.service';
import { DialogRegisterNegotiationComponent } from '../dialogs/dialog-register-negotiation/dialog-register-negotiation.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DialogChatNegotiationComponent } from '../dialogs/dialog-chat-negotiation/dialog-chat-negotiation.component';
import { ActivatedRoute } from '@angular/router';
import { EncryptionUtil } from '../utils/encryption.util';
import { ProfileProviderPJCacheListService } from '../services/profile-provider-pj-cache-list.service';
import { ProfileProviderPFCacheListService } from '../services/profile-provider-pf-cache-list.service';
import { EnumDescriptionPipe } from '../pipes/enum-description.pipe';
import { ContainerComponent } from '../container/container.component';
register();

export interface DialogData {
  newNegotiation: Negotiation;
}

@Component({
    selector: 'app-negotiation-panel',
    standalone: true,
    imports: [MaterialModule, FormsModule, EnumDescriptionPipe, ContainerComponent],
    templateUrl: './negotiation-panel.component.html',
    styleUrl: './negotiation-panel.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class NegotiationPainelComponent implements OnInit {
  readonly NegotiationStatusEnum = Negotiation.StatusEnum;

  negotiationService = inject(NegotiationControllerService);
  chatNegotiationsService = inject(ChatNegotiationControllerService);
  deviceService = inject(DeviceDetectorService);
  dialog = inject(MatDialog);
  UserPreferencesCacheService = inject(UserPreferencesCacheService);
  cd = inject(ChangeDetectorRef);
  authCustomService = inject(AuthCustomService);
  snackBar = inject(MatSnackBar);
  route = inject(ActivatedRoute);
  private readonly profileProviderPFCacheListService = inject(ProfileProviderPFCacheListService);
  private readonly profileProviderPJCacheListService = inject(ProfileProviderPJCacheListService);

  participantCpfCnpj!: string | null;

  selectedTab = new FormControl('');
  @ViewChild('swiper') swiperRef!: ElementRef<SwiperContainer>;
  newNegotiation!: Negotiation;
  isMobile!: boolean;
  displayedColumnsOpen: string[] = [
    'id',
    'initiatorCpfCnpj',
    'recipientCpfCnpj',
    'createdAt',
    'status',
    'buttons',
  ];
  displayedColumnsClose: string[] = [
    'id',
    'initiatorCpfCnpj',
    'recipientCpfCnpj',
    'createdAt',
    'updatedAt',
    'status',
    'buttons',
  ];

  negotiations: Negotiation[] = [];
  isLoading = true;

  async ngOnInit() {
    this.isMobile = this.deviceService.isMobile();
    this.participantCpfCnpj = EncryptionUtil.decrypt(this.route.snapshot.paramMap.get('participantCpfCnpj') || '');
    if (this.participantCpfCnpj) {
      this.negotiationService.getNegotiationsByParticipant(this.participantCpfCnpj).subscribe({
        next: (negotiations) => {
          this.negotiations = negotiations || [];
          this.isLoading = false;
          this.cd.detectChanges();
        },
        error: (err) => {
          console.error('Error loading negotiations:', err);
          console.error('Error details:', {
            status: err.status,
            message: err.message,
            url: err.url
          });
          this.negotiations = [];
          this.isLoading = false;
          this.cd.detectChanges();
        },
      });
    } else {
      console.warn('No participantCpfCnpj found');
      this.negotiations = [];
      this.isLoading = false;
      this.cd.detectChanges();
    }

    this.selectedTab.setValue('tab1');
  }

  ngAfterViewInit() {
    this.initializeSwiperWithRetry();
  }

  private initializeSwiperWithRetry(attempts = 0, maxAttempts = 10) {
    setTimeout(() => {
      const swiperInstance = this.swiperRef?.nativeElement?.swiper;
      
      if (swiperInstance) {
        this.setupSwiperListeners(swiperInstance);
      } else {
        if (attempts < maxAttempts) {
          this.initializeSwiperWithRetry(attempts + 1, maxAttempts);
        }
      }
    }, 200 * (attempts + 1));
  }

  private setupSwiperListeners(swiperInstance: any) {
    this.selectedTab.valueChanges.subscribe((value) => {
      const index = ({
        tab1: 0,
        tab2: 1,
      } as Record<string, number>)[value ?? 'tab1'];

      if (typeof index === 'number' && swiperInstance.activeIndex !== index) {
        swiperInstance.slideTo(index);
      }
    });

    swiperInstance.on('slideChange', () => {
      const idx = swiperInstance.activeIndex;
      const tabValue = ['tab1', 'tab2'][idx] || 'tab1';

      if (this.selectedTab.value !== tabValue) {
        this.selectedTab.setValue(tabValue, { emitEvent: false });
        this.cd.detectChanges();
      }
    });
  }

  createRegisterNegotiation() {
    const dialogRef = this.dialog.open(DialogRegisterNegotiationComponent, {
      data: {
        negotiation: this.newNegotiation,
        participantCpfCnpj: this.participantCpfCnpj
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.negotiations.push(result);
        this.cd.detectChanges();
      }
    });
  }

  countNotRead(id: number) {
    let chats = this.negotiations.find((n) => n.id == id)?.chatsNegotiations;
    let noRead: number = 0;
    chats?.forEach((chat) => {
      if (
        !chat.readByRecipient &&
        chat.senderCpfCnpj &&
        this.participantCpfCnpj &&
        chat.senderCpfCnpj != this.participantCpfCnpj
      ) {
        noRead = noRead + 1;
      }
    });

    return noRead > 0 ? noRead : '';
  }

  openDialogChat(negotiationId: number) {
    let negotiation = this.negotiations.find(
      (n) => n.id == negotiationId
    );

    if (this.participantCpfCnpj) {
      this.chatNegotiationsService
        .markMessagesAsRead(negotiationId, this.participantCpfCnpj)
        .subscribe({
          next: () => {
            this.setWasRead(negotiationId);
            this.cd.detectChanges();
          },
          error: (err) => {
            console.error('Erro ao marcar mensagens como lidas:', err);
          }
        });
    }

    const dialogConfig: any = {
      data: {
        negotiation: negotiation,
        participantCpfCnpj: this.participantCpfCnpj
      },
    };

    if (this.isMobile) {
      dialogConfig.position = { bottom: '0px' };
    } 
    const dialogRef = this.dialog.open(DialogChatNegotiationComponent, dialogConfig);

    dialogRef.afterClosed().subscribe();
    this.cd.detectChanges();
  }

  setWasRead(negotiationId: number) {
    this.negotiations.forEach((n) => {
      if (n.id === negotiationId) {
        n.chatsNegotiations?.forEach((chat) => {
          if (!chat.readByRecipient) {
            chat.readByRecipient = true;
          }
        });
      }
    });
  }

  getNegotiationsClosed() {
    const closed = this.negotiations.filter((n) =>
      n.status && (
        n.status === Negotiation.StatusEnum.Completed ||
        n.status === Negotiation.StatusEnum.Cancelled ||
        n.status === Negotiation.StatusEnum.Rejected
      )
    );
    return closed;
  }

  getNegotiationsOpen() {
    const open = this.negotiations.filter((n) =>
      n.status && (
        n.status === Negotiation.StatusEnum.Accepted ||
        n.status === Negotiation.StatusEnum.InProgress ||
        n.status === Negotiation.StatusEnum.Pending
      )
    );
    return open;
  }

  changeStatusToAccepted(negotiationId: number): void {
    if (!negotiationId) {
      this.snackBar.open('ID da negociação inválido', 'Fechar', { duration: 3000 });
      return;
    }

    this.negotiationService.updateNegotiationStatus(negotiationId, 'ACCEPTED')
      .subscribe({
        next: (updatedNegotiation) => {
          const index = this.negotiations.findIndex(n => n.id === negotiationId);
          if (index !== -1) {
            this.negotiations[index] = updatedNegotiation;
          }

          this.snackBar.open('Status alterado para Aceito com sucesso!', 'Fechar', { duration: 3000 });
          this.cd.detectChanges();
        },
        error: (err) => {
          console.error('Erro ao alterar status da negociação:', err);
          this.snackBar.open('Erro ao alterar status da negociação', 'Fechar', { duration: 3000 });
        }
      });
  }

  changeStatusToRejected(negotiationId: number): void {
    if (!negotiationId) {
      this.snackBar.open('ID da negociação inválido', 'Fechar', { duration: 3000 });
      return;
    }

    this.negotiationService.updateNegotiationStatus(negotiationId, 'REJECTED')
      .subscribe({
        next: (updatedNegotiation) => {
          const index = this.negotiations.findIndex(n => n.id === negotiationId);
          if (index !== -1) {
            this.negotiations[index] = updatedNegotiation;
          }

          this.snackBar.open('Status alterado para Rejeitado com sucesso!', 'Fechar', { duration: 3000 });
          this.cd.detectChanges();
        },
        error: (err) => {
          console.error('Erro ao alterar status da negociação:', err);
          this.snackBar.open('Erro ao alterar status da negociação', 'Fechar', { duration: 3000 });
        }
      });
  }

  changeStatusToClose(negotiationId: number): void {
    if (!negotiationId) {
      this.snackBar.open('ID da negociação inválido', 'Fechar', { duration: 3000 });
      return;
    }

    this.negotiationService.updateNegotiationStatus(negotiationId, 'COMPLETED')
      .subscribe({
        next: (updatedNegotiation) => {
          const index = this.negotiations.findIndex(n => n.id === negotiationId);
          if (index !== -1) {
            this.negotiations[index] = updatedNegotiation;
          }

          this.snackBar.open('Negociação encerrada com sucesso!', 'Fechar', { duration: 3000 });
          this.cd.detectChanges();
        },
        error: (err) => {
          console.error('Erro ao encerrar negociação:', err);
          this.snackBar.open('Erro ao encerrar negociação', 'Fechar', { duration: 3000 });
        }
      });
  }

  
  changeStatusToCanceled(negotiationId: number): void {
    if (!negotiationId) {
      this.snackBar.open('ID da negociação inválido', 'Fechar', { duration: 3000 });
      return;
    }

    this.negotiationService.updateNegotiationStatus(negotiationId, 'CANCELLED')
      .subscribe({
        next: (updatedNegotiation) => {
          const index = this.negotiations.findIndex(n => n.id === negotiationId);
          if (index !== -1) {
            this.negotiations[index] = updatedNegotiation;
          }

          this.snackBar.open('Negociação cancelata com sucesso!', 'Fechar', { duration: 3000 });
          this.cd.detectChanges();
        },
        error: (err) => {
          console.error('Erro ao encerrar negociação:', err);
          this.snackBar.open('Erro ao encerrar negociação', 'Fechar', { duration: 3000 });
        }
      });
  }
  

  getProfilePFDto(cpf: string): ProfileProviderPF {
    const profilePF = this.profileProviderPFCacheListService.findProfileProviderPFByCpf(cpf);
    return profilePF ? profilePF : { firstName: 'Usuário não encontrado' } as ProfileProviderPF;
  }

  getCompanyDto(cnpj: string): ProfileProviderPJ {
    const pj =
      this.profileProviderPJCacheListService.findProfileProviderPJByCnpj(
        cnpj
      );
    return pj ? pj : { companyName: 'N/A' } as ProfileProviderPJ;
  }

}
